import { DbSchemeEnum } from "src/app/enums/db-scheme.enum";
import { PaymentStatusEnum } from "src/app/enums/payment-status.enum";
import { ProcedureNameEnum } from "src/app/enums/procedure-name.enum";
import { VirtualProductType } from "src/app/enums/virtual-product-type.enum";
import { VoucherTypeEnum } from "src/app/enums/voucher-type.enum";
import { TenderType } from "./tender-type.enum";
import { IPayment } from "./get-trip-data.model";

// Dert interfaces

export interface IPaymentAttribute {
  Key: string;
  Value: any;
}

export interface IBenefitsItem {
  Value: number;
  Count: number;
}

export interface IFilteredHeaderPayments {
  pendingPayments: IHeadersWithPaymentsPayment[];
  donePayments: IHeadersWithPaymentsPayment[];
  failedPayments: IHeadersWithPaymentsPayment[];
  allPayments: Array<ITripPayment>
}
export interface IExecuteProcedureRequest<T> {
  Parameters: T;
  ProcedureName: ProcedureNameEnum;
  DbScheme: DbSchemeEnum;
}

export interface ICreateVoucherRequestParams {
  Brand: string;
  Source: string;
  VoucherCurrency: string;
  VoucherPrice: number;
  SendEmailToOwner: boolean;
  VoucherOwner: IVoucherOwner;
  VoucherCustomerDetails?: IVoucherCustomerDetails;
  VoucherVersion: string;
  VoucherPDF: IVoucherPDF;
  IdVoucherPayment: string | null;
  VoucherType: VoucherTypeEnum | string;
  Payment: IPayment;
  CreatePaymentRecord?: boolean;
}

export interface IVoucherOwner {
  OwnerName: string;
  OwnerSurname: string;
  OwnerEmail: string;
  OwnerPhone: string;
  OwnerCustomerId: string | null;
}

export interface IVoucherCustomerDetails {
  Street?: string | null;
  City?: string | null;
  Country?: string | null;
  ZipCode?: string | null;
  CIN?: string | null;
  TIN?: string | null;
}

export interface ICreateVoucherResult {
  /*
  {
    "success": true,
    "sentEmail": true,
    "vouchers": [
        {
            "code": "223310841",
            "voucherPrice": "500.00",
            "voucherCurrency": "CZK"
        }
    ],
  "error": "Before voucher activation you must set Doklad o úhradě voucheru"
}

{
    "data": {
        "createdSuccess": true,
        "activationSuccess": true,
        "activationError": "",
        "voucherCode": "995169712",
        "docID": "",
        "paymentHeaderResult": {
            "HeaderID": 10539,
            "HeaderUID": "3FAC79E2-A9CC-4EEC-B2D6-7D75AEF2FC6F",
            "PaymentUID": "52E4D9FB-2A49-4454-9EEF-79F2E1ADC65D",
            "PaymentType": 0,
            "Status": 0,
            "PaymentValue": 98.0,
            "CurrencyISOCode": "CZK",
            "ToFinalDocuments": true
        },
        "ToFinalDocuments": true,
        "PaymentUID": "52E4D9FB-2A49-4454-9EEF-79F2E1ADC65D",
        "PosHeaderUID": "F0A64B8A-0CC2-4C88-9D68-CCEBE775BC30",
        "HeaderUID": "3FAC79E2-A9CC-4EEC-B2D6-7D75AEF2FC6F",
        "HeaderID": 10539
    },
    "result": 0
}

*/
  createdSuccess: boolean;
  activationSuccess: boolean;
  activationError?: string;
  voucherCode?: string;
  /** @deprecated Use voucherCode */
  voucherNumber?: string;
  errorMessage?: Array<string>;
  error?: string;
  paymentHeaderResult?: IInsertDertHeadersResultData | null;
  PosHeaderUID?: string | null;
  HeaderUID?: string | null;
  HeaderID?: number | null;
  PaymentUID?: string | null;
  ToFinalDocuments?: boolean;
}

export interface IGetVoucherPdfResult {
  success: boolean;
  pdfContent?: string;
  voucherBase64?: string;
  error?: string;
}

export interface IPrintVoucherResult {
  success: boolean;
  fileName: string;
  voucherBase64: string;
  contentType: string;
}

export interface IVoucherPDF {
  note: string;
}
export interface IGetTripRequestParams {
  ReservationId: string;
  HeaderUID?: string;
  VirtualProductTypes: Array<VirtualProductType>;
}

export interface IGetCashMovementsSummaryRequest { 
  CurrencyCode: string;
  PaymentName: string;
  ReturnToResult: number;
}

export interface IGetCashMovementsSummaryResponse { 
  BusinessUnitID: number;
  TotalMovement: number;
  TotalLocalMovement: number;
  RecordCount: number;
  CalculatedDateTime: Date;
  LastClosingDateTime: Date;
  CurrencyCode: string;
  PaymentName: string;
}

export interface IPrintVoucherRequest {
  paymentVariable: string;
  ownerEmail: string;
  brand: string;
  sendEmail: boolean;
  voucherVersion: string;
  voucherPdf: IVoucherPDF;
  source: string;
  idVoucherPayment: string | null;
  base64: boolean;
}

export interface ISendVoucherRequestParams {
  PaymentVariable: string;
  OwnerEmail: string;
  Brand: string;
  SendEmail: boolean;
  VoucherVersion: string;
  VoucherPDF: IVoucherPDF;
  Source: string;
}

export interface ISendVoucherResult {
  status?: string;
  message?: string;
  success?: boolean;
  error?: string;
  pdfLink?: string;
  [key: string]: any; // Allow additional properties from DERT API response
}

export interface ISetVoucherPaymentRequestParams {
  code: string;
  idVoucherPayment: string;
}

export interface ISetVoucherPaymentResult {
  success?: boolean;
  error?: string;
  message?: string;
  [key: string]: any; // Allow additional properties from DERT API response
}

export interface ITripPayerFormData {
  ordererName: string;
  ordererSurname: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  CIN: string | null;
  TIN: string | null;
}

export interface IExecuteProcedureResult<T> {
  data: T;
  result: ResultCode;
}

export interface IProcedureError {
  status: "error";
  message?: string;
}

export interface IProcedureLocalizedError extends IProcedureError {
  localizationCode: string;
}

export function isProcedureError(value: unknown): value is IProcedureError {
  return !!value
    && typeof value === "object"
    && (value as Partial<IProcedureError>).status === "error";
}

export function isProcedureLocalizedError(value: unknown): value is IProcedureLocalizedError {
  if (!isProcedureError(value)) {
    return false;
  }

  const error = value as Partial<IProcedureLocalizedError>;
  return typeof error.localizationCode === "string";
}

export interface IHeadersWithPaymentsResultData {
  Header: IHeadersWithPaymentsHeader;
  Payments: IHeadersWithPaymentsPayment[];
}

export interface IFEConfigResultData {
  NotificationRefreshMinutes?: number | null;
  NotificationPageSize?: number | null;
  Country: string;
  HelpLink?: string | null;
  CrossSaleMarketCodes?: string[] | null;
  FlightTicketVariableSymbolRegex?: string | null;
  ETravelVariableSymbolRegex?: string | null;
  BnrRegex?: string | null;
  MaxCardPercentage: number;
  MaxCardValue: number;
  MaxBenefitPercentage: number;
  MaxBenefitValue: number;
  MaxCashPercentage: number;
  MaxCashValue: number;
  MaxVoucherBenefitPrice: number;
  MaxVoucherCardPrice: number;
  MaxVoucherCashPrice: number;
}

export interface IVoucherCreateResponseData {
  success: boolean;
  vouchers: IVoucher[];
}

export interface IVoucher {
  code: string;
  voucherPrice: string;
  voucherCurrency: string;
}

export interface IVoucherStylesResponseData {
  success: boolean;
  voucherStyles: IVoucherStyle[];
}

export interface IVoucherStatusRequest {
  bnr: number;
  source: string;
}

export interface IVoucherStatusItem {
  sapId: string;
  groupType: string;
  groupTypeName: string;
  voucherPrice: string;
  voucherCurrency: string;
  generatedDate: string;
  usedIn: string;
  insurancePrice: string | null;
  insuranceCurrency: string | null;
  useInsurancePrice: string;
  useInsuranceCurrency: string;
  useDate: string;
  expirationDate: string;
}

export interface IVoucherStatusResult {
  success: boolean;
  vouchers: IVoucherStatusItem[];
}

export interface IVoucherStyle {
  dedication: boolean;
  imgUrl: string;
  voucherStyle: string;
}

export interface IConfiguration {
  notificationRefreshMinutes?: number | null;
  notificationPageSize?: number | null;
  helpLink?: string | null;
  crossSaleMarketCodes?: string[];
  flightTicketVariableSymbolRegex?: string | null;
  eTravelVariableSymbolRegex?: string | null;
  bnrRegex?: string | null;
  maxCardPaymentPercentage?: number;
  maxCardPaymentValue?: number;
  maxBenefitPaymentValue: number;
  maxBenefitPaymentPercentage: number;
  maxCashPaymentPercentage?: number;
  maxCashPaymentValue?: number;
  maxVoucherBenefitPrice?: number;
  maxVoucherCardPrice?: number;
  maxVoucherCashPrice?: number;
}

export interface IGetGdaHeaderRequestData {
  HeaderUID: string;
}

export interface IGetGdaHeaderResponseData {
  HeaderID?: number;
  HeaderUID?: string;
  PosHeaderUID?: string;
  DocumentNumber?: string;
  Status?: PaymentStatusEnum | number | string;
  PaymentUID?: string;
  ProcessingStatus?: string | number;
  processingStatus?: string | number;
  status?: string | number;
}

export class GetGdaHeaderRequest implements IExecuteProcedureRequest<IGetGdaHeaderRequestData> {
  public ProcedureName = ProcedureNameEnum.GetGdaHeader;
  public DbScheme = DbSchemeEnum.Dert;
  public Parameters: IGetGdaHeaderRequestData;

  constructor(headerUid: string) {
    this.Parameters = {
        HeaderUID: headerUid,
    };
  }
}

// =====================================================
// Reporting Services - Get PDF Report
// =====================================================

export interface IReportParameter {
  key: string;
  value: string | number;
}

export enum ReportingServicesOutputMode {
  Binary = "binary",    // Returns as VARBINARY (default)
  File = "file",        // Returns file path (temp file NOT deleted)
  Json = "json",        // Returns base64-encoded JSON
}

export interface IReportingServicesGetPdfRequestParams {
  report_name: string;
  output_mode: ReportingServicesOutputMode | string;
  params?: IReportParameter[];
}

export interface IReportingServicesGetPdfResultData {
  success: boolean;
  pdfData?: string;  // Base64-encoded PDF for 'json' mode
  filePath?: string; // File path for 'file' mode
  pdfContent?: string; // Binary data for 'binary' mode (returned as VARBINARY)
  reportName?: string;
  sizeBytes?: number;
  error?: string;
}

export class GetReportingServicesGetPdfRequest implements IExecuteProcedureRequest<IReportingServicesGetPdfRequestParams> {
  public ProcedureName = ProcedureNameEnum.ReportingServicesGetPDF;
  public DbScheme = DbSchemeEnum.Dert;
  public Parameters: IReportingServicesGetPdfRequestParams;

  constructor(
    reportName: string,
    params?: IReportParameter[],
    outputMode: ReportingServicesOutputMode | string = ReportingServicesOutputMode.Binary
  ) {
    this.Parameters = {
      report_name: reportName,
      output_mode: outputMode,
      params: params || [],
    };
  }
}

export interface ISendPdfRequestParams {
  HeaderID: number;
  targetEmail: string;
}

export class SendPdfRequest implements IExecuteProcedureRequest<ISendPdfRequestParams> {
  DbScheme = DbSchemeEnum.Dert;
  ProcedureName = ProcedureNameEnum.SendPdf;
  Parameters: ISendPdfRequestParams;

  constructor(targetEmail: string, headerId: number) {
    this.Parameters = {
      HeaderID: headerId,
      targetEmail: targetEmail,
    };
  }
}

export interface IHeadersWithPaymentsHeader {
  Updated: string;
  Created: string;
  HeaderUID: string;
  PosHeaderUID: string;
  BusinessUnitInternalNumber?: number | null;
  DocID?: string | null;
  DataBlob: any;
}

export interface IHeadersWithPaymentsPayment {
  Created: string;
  Updated: string;
  CurrencyISOCode: string;
  PaymentType: TenderType;
  PaymentName: string;
  PaymentUID: string;
  PaymentValue: number;
  Status: PaymentStatusEnum;
}

/*
"HeaderID": 10327,
            "HeaderUID": "D46A918C-8611-41AE-A933-C88E392C2E6E",
            "PaymentUID": "C991E999-0550-47DE-A662-69BFB696C51C",
            "PaymentType": 0,
            "Status": 0,
            "PaymentValue": 500.0,
            "CurrencyISOCode": "CZK",
            "ToFinalDocuments": true
 */
export interface IInsertDertHeadersResultData {
  HeaderID: number;
  HeaderUID: string;
  PaymentUID: string;
  PosHeaderUID: string;
  PaymentType: TenderType;
  PaymentName: string;
  PaymentValue: number;
  Status: PaymentStatusEnum;
  CurrencyISOCode: string;
}

export interface IGetPaymentTypesResultData {
  Availability?: import("../../models/payment-availability.model").PaymentAvailability | null;
  Name: string; // PaymentName
  Description: string;
  Currency: string;
  TenderSubType: string;
  TenderType: TenderType;
  IsPayAllowed: boolean;
  ManualEntry: boolean;
  ManagerStorno: boolean;
  IsBenefit: boolean;
  IsDenominationRequired: boolean;
  SapExport: boolean;
  ClosingToZero: boolean;
  Nominals?: IPaymentNominal[];
  IncludedItemAssortmentCode?: string | null;
  Attributes?: IKeyValue[];
}

export interface IKeyValue {
  Key: string;
  Value: string;
}

export interface IPaymentNominal {
  denomination: string;
  sequence: number;
  value: number;
}

export interface IAllowedPaymentsWithNominals {}

export interface IGetSobitTokenResultData {
  Token: string;
  ApiKey: string;
}

export interface ISobitConfig {
  apiKey: string;
  token: string;
}

export interface IDertData {
  reservation: ITripDataReservation;
  responseId: string;
  serviceTimestamp: string;
  clientCorrelationId: string;
  responseStatusObject: ITripDataResponseStatus;
  payments: ITripPayment[];
}

export interface ITripPayment {
  id: string;
  date: string;
  type: TenderType | null;
  amount: number;
  currency: string;
  status: PaymentStatusEnum
}
export interface ITripCustomer {
  name: string;
  surname: string;
  street: string;
  city: string;
  country: string;
  zipCode: string;
  phoneNumber: string;
  emailAddress: string;
  CIN: string | null;
  TIN: string | null;
}

export interface ITripDataResponseStatus {
  isSuccess: boolean;
  statusCode: number;
  errorMessage: string;
}

export interface ITripDataReservation {
  reservationId: string;
  reservationDate: string;
  statusCode: string;
  destinationName: string;
  customer: IITripDataReservationCustomer;
  paymentSummary: ITripDataReservationPaymentSummary;
}

export interface ITripDataReservationPaymentSummary {
  isPaid: boolean;
  totalPrice: ITripDataReservationPaymentSummaryTotalPrice;
  totalPaid: ITripDataReservationPaymentSummaryTotalPrice;
  paymentConditionItems: ITripDataReservationPaymentSummaryPaymentItem[];
  paymentPaidItems: ITripDataReservationPaymentSummaryPaymentItem[];
}

export interface ITripDataReservationPaymentSummaryPaymentItem {
  dueDate: string;
  paidDate: string;
  amount: number;
  currency: string;
  status: PaymentStatusEnum
}
export interface ITripDataReservationPaymentSummaryTotalPrice {
  amount: number;
  currency: string;
}

export interface IITripDataReservationCustomer {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailAddress: string;
  address: IITripDataReservationCustomerAddress;
}

export interface IITripDataReservationCustomerAddress {
  streetNo: string;
  street: string;
  city: string;
  zipCode: string;
  countryISO3Code: string;
}

export class AliveResponse {
  status: string;
  version: string;
  ipAddress: string;
}

export enum ResultCode {
  OK = 0,
  OK_NOT_EXECUTED = 1,
  OK_DONT_SELL = 2,
  ERROR_ITEM_UNKNOWN = 3,
  ERROR_NOT_AN_ITEM = 4,
  ERROR_IS_COUPON = 5,
  ERROR_IS_VOUCHER = 6,
  ERROR_IS_REFUND = 7,
  ERROR_IS_CUSTOMERCARD = 8,
  ERROR_IS_GIFTCARD = 9,
  ERROR_IS_ETOPUP = 10,
  ERROR_TRY_ALTERNATIVE = 11,
  ERROR_ILLEGAL_CALL = 12,
  ERROR_OTHER = 13,
  SIGNATURE_ACCEPTED = 14,
  SIGNATURE_DECLINED = 15,
  PRINT_REPRINT = 16,
  PRINT_RETRY = 17,
  PRINT_ABORT = 18,
  NOT_FOUND = 19,
  INCORRECT_PIN = 20,
  EXCEPTION = 21,
}

export interface ILocalizationLanguage {
  code: string;
  label: string;
}

export interface SessionInfo {
  sessionID: string;
}
export interface DiscountValueEvent {
  confirmed: boolean;
  value: number;
}
export interface RechargeValueEvent {
  confirmed: boolean;
  value: number;
  code: string;
}

export interface ISetCustomerPromotion extends SessionInfo {
  active: boolean;
  promoActionCode: string;
}

export interface IApplyAutomaticPromotion extends SessionInfo {
  automaticPromotions: {
    id: number;
    count: number;
    promotionCode: string;
  }[];
}
export interface ManualDiscountRequest extends SessionInfo {
  discountValue?: number;
  recordID: number;
  discountReason?: ManualDiscountReason;
  selectedDiscount?: ManualDiscountType;
}
export interface ManualDiscountTransactionRequest extends SessionInfo {
  discountValue: number;
  discountReason: ManualDiscountReason;
  selectedDiscount: ManualDiscountType;
}
export interface GetManualDiscountResult extends BasicResult {
  applicableDiscounts: ManualDiscountType[];
  possibleReasons: ManualDiscountReason[];
  userRoles: string[];
  hasTransactionDiscount: boolean;
}
export interface ManualDiscountReasonConfig {
  reasons: ManualDiscountReason[];
}

export interface ManualDiscountReason {
  active: boolean;
  code: string;
  description: string;
  column: number;
  row: number;
  backgroundColor: string;
  textColor: string;
  useCustomDescription: boolean;
}
export interface ManualDiscountsConfig {
  discountTypes: ManualDiscountType[];
  maximumParallelItemDiscounts: number;
  maximumParallelTransactionDiscounts: number;
  showNotificationForItemWithQuantityMoreThanOne: boolean;
  itemDicountsQuantityNotificationCustomText: string;
}
export interface GetManualDiscountRequest extends SessionInfo {
  recordID: number;
}
export enum DiscountValueType {
  Percent = 0,
  Value = 1,
  ManualPrice = 2,
}

export enum DiscountApplicationType {
  Item = 0,
  Transaction = 1,
  ItemOrTransaction = 2,
}

export interface ILocalizationLanguage {
  code: string;
  label: string;
}

export interface ManualDiscountType {
  active: boolean;
  name: string;
  code: string;
  discountValueType: DiscountValueType;
  value: number;
  valueList: number[];
  discountApplicationType: DiscountApplicationType;
  reasonRequired: boolean;
  possibleReasonCodes: string[];
  applicableOnActionPriceItems: boolean;
  applicableOnNondiscountableItems: boolean;
  applicableOnManualDiscountedItems: boolean;
  applicableOnManualPriceChangeItems: boolean;
  combinableWithDiscountCodes: string[];
  applicableOnPromotionDiscountedItems: boolean;
  combinableWithPromotionCodes: string[];
  requiredRoles: string[];
  requiresConfirmation: boolean;
  requiresConfirmationRoles: string[];
  requiredMinRemainingValue?: number;
  column: number;
  row: number;
  backgroundColor: string;
  textColor: string;
  applicableOnProductsWithAttributes: { [key: string]: string };
  notApplicableOnProductsWithAttributes: { [key: string]: string };
  enableValueOverride: boolean;
}
export interface ManualDiscountRequestModel {
  discountValue: number;
  discountReason: string;
}
export interface ManualDiscountResult extends BasicResult {
  isDiscounted: boolean;
}
export interface ItemImageRequest {
  itemNumber: string;
}
export interface GetItemImageResult extends BasicResult {
  image: string;
  imageUrl: string;
}
export interface HardwareScannerResult {
  result: any;
}
export interface SendApiConfigResult {
  result: any;
}
export interface AuthResult {
  access_token: string;
  token_type: string;
}
export interface SendApiConfigRequest {
  ipAddress: string;
  configData: PosApiConfig;
  apiIpAddress: string;
  isLoggedIn: boolean;
}
export interface isLoggedInRequest {
  isLoggedIn: boolean;
}
export interface HasReservationRequest {
  hasReservation: boolean;
}
export interface CompletedOrderRequest {
  orderNumber: string;
}
export interface GetConfigRequest extends SessionInfo {}

export interface CustomerExternalTransactionsByNumberRequest
  extends SessionInfo {
  number: string;
  type: TransactionType;
}
export interface GetConfigResult extends BasicResult {
  configData: ItemInfoConfig;
}
export interface GetPosApiConfigResult extends BasicResult {
  configData: PosApiConfig;
}
export interface PosApiConfig {
  enableRestAPI: boolean;
  enableTPiScanAPI: boolean;
  port: number;
  bindToLocalIPAddress: boolean;
  bindTo: string[];
  iScanCfg: TPiScanApiConfig;
  intelexApiIpAddress: string;
  intelexApiPort: number;
  enableSendToIntelex: boolean;
  userName: string;
  password: string;
  tokenValidityHours: number;
  logOutAfterMinutes: number;
  cashPaymentAllowed: boolean;
  webPosUseScannerOnTransactionStart?: boolean;
  webPosFiscalIpAddress?: string;
  webPosActiveCustomerIconColor?: string;
  webPosQuickItemSelectAutoOpen?: number;
  webPosQuickItemSelectColumns?: number;
  quickPrintChangeAllowed?: boolean;
}
export interface PosApiConfigAuth {
  username: string;
  password: string;
  tokenValidityHours: number;
}

export interface TPiScanApiConfig {
  mainHostName: string;
  mainPort: number;
  cancelHostName: string;
  cancelPort: number;
  posEventsHostName: string;
  posEventsPort: number;
  cancelPosEventsHostName: string;
  cancelPosEventsPort: number;
}
export interface ItemInfoConfig extends ItemDetail {
  formConfig: ItemInfoFormConfig;
}

export enum ValueType {
  itemIdentityId = 0,
  itemNumber = 1,
  itemEanCode = 2,
  itemDescription = 3,
  itemDescriptions = 4,
  itemPrice = 5,
  referenceUnit = 6,
  minimumCustomerAge = 7,
  minimumEmployeeAge = 8,
  highRisk = 9,
  tareWeight = 10,
  weighingRequired = 11,
  dimensionsInputRequired = 12,
  priceVerifyRequired = 13,
  priceEntryRequired = 14,
  quantityEntryRequired = 15,
  quantityDisallowed = 16,
  priceOverrideAllowed = 17,
  notForSale = 18,
  notForScoSale = 19,
  pharmacyItem = 20,
  availablePictures = 21,
  attribtues = 22,
}

export interface ItemInfoFormConfig {
  fields: ItemInfoFieldConfig[];
}

export interface ItemInfoFieldConfig {
  caption: string;
  fieldType: ValueType;
  column: number;
  value: string;
}
export interface ColumnSizes {
  column1: any;
  column2: any;
  image: any;
}
export enum TransactionType {
  Undefined = 0,
  EshopReservation = 1,
}

export interface Message {
  untranslatableText: string;
  translatableTextKey: string;
  translatableTextParams: string[];
}

export interface BasicResult {
  result: ResultCode;
  failureReason: Message;
  transactionStarted: boolean;
  transactionStartErrorMessage: string;
}
export interface VoucherValueChangeRequest extends SessionInfo {
  ean: string;
  value: number;
}
export interface HardwareScannerResult {
  response: string;
}

export interface ItemInfoRequest extends SessionInfo {
  itemIdentityId: number;
  quantity: number;
  weight?: number;
  referencePricePerUnit?: number;
  finalPrice?: number;
  forceUseOfFinalPrice?: boolean;
  salesReturn: boolean;
  responses?: DataInputOrConfirmationResponse[];
}
export interface ItemQuantityRequest extends SessionInfo {
  quantity: number;
  ean: string;
  position: number;
}
export interface ItemVoidRequest extends SessionInfo {
  itemID: number;
}
export interface ItemDetailInfoRequest extends SessionInfo {
  itemCode: string;
  codeType: ItemCodeTypes;
  scanDataType?: number;
  msrData?: MsrData;
  rfidData?: RFIDData;
  salesReturn?: boolean;
  entryMethod?: EntryMethods;
}
export enum ItemCodeTypes {
  Unspecified = 0,
  Barcode = 1,
  ItemNumber = 2,
}
export enum EntryMethods {
  UNKNOWN = 0,
  CUSTOMER_KEYED = 1,
  CUSTOMER_SCANNED = 2,
  CUSTOMER_SELECTED = 3,
  CUSTOMER_SWIPED = 4,
  CUSTOMER_RFID = 5,
  ATTENDANT_KEYED = 6,
  ATTENDANT_SCANNED = 7,
  ATTENDANT_SELECTED = 8,
  ATTENDANT_SWIPED = 9,
  ATTENDANT_RFID = 10,
  NON_USER = 11,
}
export interface DataInputOrConfirmationResponse {
  responseId: string;
  response: string;
  msrData: MsrData;
  rfidData: RFIDData;
  multipleSelectionResponse: string[];
  scanDataType: number;
  entryMethod: string;
}
export interface MsrData {
  accountNr: string;
  expiryDate: string;
  track1: string;
  track2: string;
  track3: string;
}
export interface RFIDData {
  id: string;
  userData: string;
}

export interface FullTextRequest {
  ean?: string;
  vart?: string;
  fulltext: string;
  currentPage: number;
  pageSize: number;
  saleableItemsOnly?: boolean;
  itemIdentityOnlyActive?: boolean;
  pricelistId?: number;
  groupIds?: number[];
  cultureInfoName?: string;
  businessUnitInternalNumber?: number;
}
export interface FullTextSearchResult extends BasicResult {
  itemDetails: VariantItems[];
  totalCount: number;
  itemCount: number;
}
export interface ApiQuickSelectResult extends BasicResult {
  groups: ApiQuickSelectCategoryConfig[];
}

export interface AvailableCurrenciesResult {
  currency: string;
}
export interface VariantItems {
  variants: ItemDetail[];
  mainItem: ItemDetail;
  variantCodes: string[];
  variantType: VariantType;
}
export interface Variant {
  iD: number;
  created: string;
  variantType: VariantType;
  description: string;
  code: string;
  isDefault: boolean;
  variantsToStockItems: VariantToStockItem[];
  externalID: string;
}
export interface VariantToStockItem {
  iD: number;
  created: string;
  variantID: number;
  variant: Variant;
  stockItemID: number;
  stockItem: StockItem;
  displayOrder: number | null;
}
export interface StockItem extends Item {
  sKU: string;
  variantsToStockItems: VariantToStockItem[];
  customsCode: string;
}
export interface MeasurementUnit {
  iD: number;
  created: string;
  code: string;
  decimalPlaces: number;
  translations: MeasurementUnitTranslation[];
  multipleQuantityCoeficient: number | null;
}
export interface MeasurementUnitTranslation {
  iD: number;
  created: string;
  description: string;
  culture: string;
  measurementUnitID: number;
  measurementUnit: MeasurementUnit;
}
export interface Item {
  iD: number;
  created: string;
  parentID: number;
  parent: VirtualItem;
  status: ItemStatus;
  brandID: number | null;
  brand: Brand;
  measurementUnitID: number;
  measurementUnit: MeasurementUnit;
  measurementUnitDecimalPlaces: number | null;
  defaultDescription: string;
  identities: ItemIdentity[];
  hierarchies: ItemGroupNode[];
  variants: Variant[];
  selectionBUChoices: SelectionItemBusinessUnitChoice[];
  translations: ItemTranslation[];
  itemToItemGroupNodes: ItemToItemGroupNode[];
  itemCharacteristics: ItemCharacteristic[];
  isDeleted: boolean;
  isDefault: boolean;
  noOrder: boolean;
  noSale: boolean;
  minOrderQuantity: number | null;
  maxOrderQuantity: number | null;
  saleOut: boolean;
}
export interface ItemCharacteristic {
  iD: number;
  created: string;
  itemID: number;
  businessUnitInternalNumber: number;
  noOrder: boolean;
  noSale: boolean;
  minOrderQuantity: number | null;
  maxOrderQuantity: number | null;
  saleOut: boolean;
  item: Item;
}
export interface ItemToItemGroupNode {
  iD: number;
  created: string;
  itemID: number;
  item: Item;
  itemGroupNodeID: number;
  itemGroupNode: ItemGroupNode;
  timeStamp: string;
}
export interface ItemTranslation {
  iD: number;
  created: string;
  itemID: number;
  item: Item;
  culture: string;
  description: string;
}
export interface SelectionItemBusinessUnitChoice {
  iD: number;
  created: string;
  selectionItemID: number;
  selectionItem: SelectionItem;
  virtualItemID: number | null;
  virtualItem: VirtualItem;
  businessUnitInternalNumber: number;
}
export interface SelectionItem extends ServiceItemBase {
  assortment: Assortment;
  assortmentID: number;
  selectionItemBusinessUnitChoices: SelectionItemBusinessUnitChoice[];
}
export interface Assortment {
  iD: number;
  created: string;
  updated: string;
  code: string;
  assortmentGroupID: number | null;
  assortmentGroup: AssortmentGroup;
  translations: AssortmentTranslation[];
}
export interface AssortmentGroup {
  iD: number;
  created: string;
  code: string;
  translations: AssortmentGroupTranslation[];
  assortments: Assortment[];
}
export interface AssortmentTranslation {
  iD: number;
  created: string;
  assortmentID: number;
  assortment: Assortment;
  culture: string;
  name: string;
  description: string;
}
export interface AssortmentGroupTranslation {
  iD: number;
  created: string;
  assortmentGroupID: number;
  assortmentGroup: AssortmentGroup;
  culture: string;
  name: string;
  description: string;
}
export interface ServiceItemBase extends Item {
  noParking: boolean;
}
export interface ItemGroupNode {
  iD: number;
  created: string;
  code: string;
  description: string;
  translations: ItemGroupNodeTranslation[];
  validFrom: string | null;
  validTo: string | null;
  children: ItemGroupHierarchy[];
  reasonCode: string;
  fullHierarchy: string;
  itemToItemGroupNodes: ItemToItemGroupNode[];
  itemGroupNodeCounters: ItemGroupNodeCounter[];
  itemGroupNodeToItemGroupAssortments: ItemGroupNodeToItemGroupAssortment[];
}
export interface ItemGroupNodeToItemGroupAssortment {
  iD: number;
  created: string;
  itemGroupNodeID: number;
  itemGroupNode: ItemGroupNode;
  itemGroupAssortmentID: number;
  itemGroupAssortment: ItemGroupAssortment;
}
export interface ItemGroupAssortment extends Assortment {
  itemGroupNodeToItemGroupAssortments: ItemGroupNodeToItemGroupAssortment[];
}
export interface Pricelist {
  iD: number;
  created: string;
  currencyID: number;
  businessUnitGroupID: number;
  countryID: number | null;
  itemIdentities: ItemIdentity[];
}
export interface ItemGroupNodeCounter {
  iD: number;
  created: string;
  pricelistID: number;
  pricelist: Pricelist;
  itemGroupNodeID: number;
  itemGroupNode: ItemGroupNode;
  countItems: number;
}
export interface ItemGroupNodeTranslation {
  iD: number;
  created: string;
  itemGroupNodeID: number;
  itemGroupNode: ItemGroupNode;
  culture: string;
  name: string;
  description: string;
}
export interface ItemGroupHierarchy extends ItemGroupNode {
  parentID: number | null;
  parent: ItemGroupNode;
  level: number;
}
export interface ItemIdentity {
  iD: number;
  timestamp: string;
  created: string;
  itemID: number;
  item: Item;
  pricelistID: number;
  pricelist: Pricelist;
  gtin: string;
  measurementUnitID: number;
  measurementUnit: MeasurementUnit;
  measurementUnitDecimalPlaces: number | null;
  itemTaxGroups: ItemTaxGroup[];
  prices: ItemPrice[];
  itemLookupCodes: ItemLookupCode[];
  originCountryISOCode: string;
  itemMeasurementUnitConversion: ItemMeasurementUnitConversion;
  itemIdentityMeasurementUnitConversions: ItemIdentityMeasurementUnitConversion[];
  physicalProperties: ItemIdentityPhysicalProperties;
  itemIdentityToItemRules: ItemIdentityToItemRule[];
  isActive: boolean;
  priceExceptions: ItemPriceException[];
}
export interface ItemPriceException {
  iD: number;
  created: string;
  itemIdentityID: number;
  itemIdentity: ItemIdentity;
  validFrom: string | null;
  validTo: string | null;
  unitQuantity: number;
  value: number;
  type: PriceType;
  noDiscount: boolean;
  priceLevel: number;
  businessUnitGroupID: number;
}
export interface ItemIdentityToItemRule {
  iD: number;
  created: string;
  itemIdentity: ItemIdentity;
  itemIdentityID: number;
  itemRuleID: number | null;
  itemRule: ItemRule;
  validFrom: string;
  validTo: string;
}
export interface ItemRule {
  iD: number;
  created: string;
  translations: ItemRuleTranslation[];
  code: string;
  isDiscountable: boolean;
  isFixedPrice: boolean;
  isSaleable: boolean;
  canNotBeSoldIndependently: boolean;
  isReturnable: boolean;
  isNonfiscal: boolean;
  isOrderable: boolean;
  isNegative: boolean;
  isDepreciation: boolean;
  service: boolean;
  noSale: boolean;
}
export interface ItemRuleTranslation {
  iD: number;
  created: string;
  itemIdentityRuleID: number;
  itemIdentityRule: ItemRule;
  languageCode: string;
  name: string;
  description: string;
}
export interface ItemIdentityPhysicalProperties extends PhysicalProperties {
  itemIdentityID: number;
  itemIdentity: ItemIdentity;
}
export interface PhysicalProperties {
  iD: number;
  created: string;
  depth: number | null;
  width: number | null;
  height: number | null;
  diameter: number | null;
  grossWeight: number | null;
  netWeight: number | null;
  drainedWeight: number | null;
  tareWeight: number | null;
  density: number | null;
  weightUnitOfMeasurementCode: string;
  sizeUnitOfMeasurementCode: string;
}
export interface ItemIdentityMeasurementUnitConversion {
  iD: number;
  created: string;
  ratio: number;
  itemIdentityID: number;
  itemIdentity: ItemIdentity;
  targetItemIdentityID: number;
  targetItemIdentity: ItemIdentity;
}
export interface ItemMeasurementUnitConversion {
  iD: number;
  created: string;
  ratio: number;
  itemIdentityID: number;
  itemIdentity: ItemIdentity;
}
export interface ItemLookupCode {
  iD: number;
  created: string;
  itemIdentityID: number;
  itemIdentity: ItemIdentity;
  value: string;
  type: ItemLookupCodeType;
  virtualItemID: number | null;
  virtualItem: VirtualItem;
}
export enum ItemLookupCodeType {
  Ean,
  Vart,
  SKU,
  MatchCode,
}
export interface ItemPrice {
  iD: number;
  created: string;
  itemIdentityID: number;
  itemIdentity: ItemIdentity;
  validFrom: string | null;
  validTo: string | null;
  unitQuantity: number;
  value: number;
  type: PriceType;
  noDiscount: boolean;
  priceLevel: number;
}
export enum PriceType {
  Unknown = 0,
  RegularPrice = 1,
  ActionPrice = 2,
  VolumeDiscountPrice = 3,
  LowerTaxPrice = 4,
  PurchasePrice = 5,
}
export interface TaxRate {
  iD: number;
  created: string;
  name: string;
  taxID: number;
  tax: Tax;
  taxRules: TaxRule[];
}
export interface TaxRule {
  iD: number;
  created: string;
  name: string;
  value: number;
  taxRateID: number;
  taxRate: TaxRate;
  validFrom: string | null;
  validTo: string | null;
  taxRuleKind: NumberValueKind;
  isValidNow: boolean;
}
export enum NumberValueKind {
  Percentage = 0,
  Fixed,
}
export interface TaxJurisdiction {
  iD: number;
  created: string;
  countryID: number;
  taxes: Tax[];
}
export interface Tax {
  iD: number;
  created: string;
  name: string;
  taxJurisdictionID: number;
  taxJurisdiction: TaxJurisdiction;
  taxRates: TaxRate[];
}
export interface ItemTaxGroup {
  iD: number;
  created: string;
  taxRateID: number | null;
  taxRate: TaxRate;
  itemIdentityID: number;
  itemIdentity: ItemIdentity;
  taxRateValidFrom: string;
  taxRateValidTo: string;
  isDefault: boolean;
  isValidNow: boolean;
  itemTaxGroupToReasonCodes: ItemTaxGroupToReasonCode[];
}
export interface ItemTaxGroupToReasonCode {
  iD: number;
  created: string;
  itemTaxGroupID: number;
  itemTaxGroup: ItemTaxGroup;
  taxReasonCodeID: number;
  taxReasonCode: TaxReasonCode;
}
export interface TaxReasonCode {
  iD: number;
  created: string;
  code: string;
  itemTaxGroupToReasonCodes: ItemTaxGroupToReasonCode[];
}
export interface Brand {
  iD: number;
  created: string;
  code: string;
  description: string;
}
export enum ItemStatus {
  Active,
  Inactive,
}
export interface VirtualItem {
  iD: number;
  timestamp: string;
  created: string;
  businessKey: string;
  matchcode: string;
  linkedVirtualItemID: number | null;
  linkedVirtualItem: VirtualItem;
  predecessorVirtualItemID: number | null;
  predecessorVirtualItem: VirtualItem;
  disabled: boolean;
  saleToNegativeStockDisabled: boolean;
  isNotDiscountable: boolean;
  isNegative: boolean;
  isNonfiscal: boolean;
  isNotReturnable: boolean;
  isNotSellable: boolean;
  isNotSellableIndependently: boolean;
  isFixedPrice: boolean;
  isService: boolean;
  attributes: Attribute[];
  supplierItems: SupplierItem[];
  translations: VirtualItemTranslation[];
  items: Item[];
  virtualItemToVirtualItemAssortment: VirtualItemToVirtualItemAssortment[];
  virtualItemTimestamps: VirtualItemTimestamp[];
  virtualItemTimestampPricelists: VirtualItemTimestampPricelist[];
}
export interface VirtualItemTimestamp {
  iD: number;
  virtualItemID: number;
  virtualItem: VirtualItem;
  lastTimestamp: number;
}
export interface VirtualItemTimestampPricelist {
  iD: number;
  virtualItemID: number;
  virtualItem: VirtualItem;
  pricelistID: number;
}
export interface VirtualItemToVirtualItemAssortment {
  iD: number;
  created: string;
  virtualItemAssortmentID: number;
  virtualItemAssortment: VirtualItemAssortment;
  virtualItemID: number;
  virtualItem: VirtualItem;
  virtualItemToTimePeriodAssignments: VirtualItemToTimePeriodAssignment[];
}
export interface VirtualItemToTimePeriodAssignment
  extends ItemAssortmentItemToTimePeriodAssignment {
  virtualItemToVirtualItemAssortmentID: number;
  virtualItemToVirtualItemAssortment: VirtualItemToVirtualItemAssortment;
}
export interface VirtualItemAssortment extends ItemAssortment {
  virtualItemToVirtualItemAssortment: VirtualItemToVirtualItemAssortment[];
}
export interface ItemAssortment extends Assortment {
  validityTimePeriod: ItemAssortmentTimePeriod[];
  assortmentToBusinessUnit: AssortmentToBusinessUnit[];
}
export interface AssortmentToBusinessUnit {
  iD: number;
  created: string;
  businessUnitInternalNumber: number;
  assortmentID: number;
  assortment: VirtualItemAssortment;
}
export interface ItemAssortmentTimePeriod {
  iD: number;
  created: string;
  description: string;
  itemAssortmentID: number;
  itemAssortment: ItemAssortment;
  validFrom: string;
  validTo: string;
  itemAssortmentItemToTimePeriodAssignments: ItemAssortmentItemToTimePeriodAssignment[];
}
export interface ItemAssortmentItemToTimePeriodAssignment {
  iD: number;
  created: string;
  timePeriodID: number;
  timePeriod: ItemAssortmentTimePeriod;
}
export interface VirtualItemTranslation {
  iD: number;
  created: string;
  virtualItemID: number;
  virtualItem: VirtualItem;
  culture: string;
  name: string;
}
export interface SupplierItem {
  iD: number;
  created: string;
  virtualItemID: number;
  virtualItem: VirtualItem;
  itemID: number | null;
  item: Item;
  isDefault: boolean;
  supplierBusinessKey: string;
  supplierBusinessKeyType: ItemLookupCodeType;
  name: string;
  description: string;
  purchasePricelistRecords: PurchasePricelistRecord[];
  supplierNumber: string;
  originCountryISOCode: string;
  measurementUnitCode: string;
  measurementUnitDecimalPlaces: number;
  stockMeasurementUnitCode: string;
  conversionRatio: number | null;
  purchasePrice: number;
}
export interface PurchasePricelistRecord {
  iD: number;
  created: string;
  dealID: number;
  deal: Deal;
  externalID: string;
  orderDateFrom: string | null;
  orderDateTo: string | null;
  supplierItemID: number;
  supplierItem: SupplierItem;
  purchasePrices: PurchasePrice[];
  attributes: PurchasePricelistRecordAttribute[];
  purchasePricelistRecordToBusinessUnitGroups: PurchasePricelistRecordToBusinessUnitGroup[];
}
export interface PurchasePricelistRecordToBusinessUnitGroup {
  iD: number;
  created: string;
  purchasePricelistRecordID: number;
  purchasePricelistRecord: PurchasePricelistRecord;
  businessUnitGroupID: number;
  businessUnitExternalID: string;
  state: AllowedBlockedType;
}
export enum AllowedBlockedType {
  Allowed,
  Blocked,
}
export interface PurchasePricelistRecordAttribute {
  iD: number;
  created: string;
  attributeTypeID: number;
  attributeType: PurchasePricelistRecordAttributeType;
  parentRecordID: number;
  parentRecord: PurchasePricelistRecord;
  value: string;
}
export interface PurchasePricelistRecordAttributeType {
  iD: number;
  created: string;
  name: string;
  description: string;
}
export interface PurchasePrice {
  iD: number;
  created: string;
  purchasePricelistRecordID: number;
  purchasePricelistRecord: PurchasePricelistRecord;
  currencyCode: string;
  unitQuantity: number;
  value: number;
  priceIsInStockMeasurementUnit: boolean;
  scalePrices: PurchasePriceScale[];
  validFrom: string | null;
  validTo: string | null;
  purchasePriceSurcharges: PurchasePriceSurcharge[];
  minimalOrderQuantity: number;
  deliveryTimeInDays: number;
  toleranceOverLimitQuantity: number;
  toleranceUnderLimitQuantity: number;
  orderDecisiveDate: DecisiveDate;
  isScalePrice: boolean;
  purchasePriceRegions: PurchasePriceCountryRegion[];
  incoterm: string;
  incotermValidityPlace: string;
}
export interface PurchasePriceCountryRegion extends PurchasePriceRegion {
  countryISOCode: string;
}
export interface PurchasePriceRegion {
  iD: number;
  created: string;
  purchasePriceID: number;
  purchasePrice: PurchasePrice;
}
export enum DecisiveDate {
  DeliveryDate,
  OrderDate,
}
export interface PurchasePriceSurcharge {
  iD: number;
  created: string;
  name: string;
  purchasePriceID: number;
  purchasePrice: PurchasePrice;
  polarity: Polarity;
  calculationType: PurchasePriceCalculationType;
  scaleType: PurchasePriceScaleType;
  roundingType: RoundingType;
  validFrom: string | null;
  validTo: string | null;
  value: number;
  purchasePriceSurchargeScales: PurchasePriceSurchargeScale[];
  isScaleCondition: boolean;
}
export interface PurchasePriceSurchargeScale {
  iD: number;
  created: string;
  purchasePriceSurcharge: PurchasePriceSurcharge;
  purchasePriceSurchargeID: number;
  amount: number;
  value: number;
}
export enum PurchasePriceCalculationType {
  Percentage = 0,
  FixedAmount,
  Quantity,
}
export enum RoundingType {
  Arithmetic = 0,
  RoundUp,
  RoundDown,
}
export enum PurchasePriceScaleType {
  None = 0,
  Value,
  Quantity,
  GrossWeight,
  NetWeight,
  Volume,
}
export enum Polarity {
  Positive = 0,
  Negative,
  Both,
}
export interface PurchasePriceScale {
  iD: number;
  created: string;
  purchasePrice: PurchasePrice;
  purchasePriceID: number;
  amount: number;
  value: number;
}
export interface Deal {
  iD: number;
  created: string;
  name: string;
  description: string;
  pricelistRecords: PurchasePricelistRecord[];
}
export interface AttributeType {
  iD: number;
  created: string;
  name: string;
}
export interface Attribute {
  iD: number;
  created: string;
  attributeTypeID: number;
  virtualItemID: number;
  itemID: number | null;
  value: string;
  attributeType: AttributeType;
  virtualItem: VirtualItem;
  item: Item;
}
export enum VariantType {
  Default = 0,
  Size = 1,
  Color = 2,
  Style = 3,
}
export interface VoidEntryResult extends BasicResult {}
export interface GetItemDetailResult extends BasicResult {
  itemDetail: ItemDetail;
}
export interface GetVariantRequest extends SessionInfo {
  ean: string;
}
export interface GetVariantResult extends BasicResult {
  item: VariantItems;
}
export interface UpdateVariantRequest extends SessionInfo {
  ean: string;
  variant: string;
  variantEan: string;
  originalItemID: number;
  itemIdentityId: number;
  position: number;
}
export interface UpdateVariantResult extends BasicResult {}

export enum CodeType {
  Ean = 0,
  Vart = 1,
}

export interface ItemDetail {
  itemIdentityId: number;
  itemNumber: string;
  itemEanCode: string;
  itemDescription: string;
  itemDescriptions: Translation[];
  itemPrice: number;
  referenceUnit: string;
  minimumCustomerAge: number;
  minimumEmployeeAge: number;
  highRisk: boolean;
  tareWeight: number;
  weighingRequired: boolean;
  dimensionsInputRequired: boolean;
  priceVerifyRequired: boolean;
  priceEntryRequired: boolean;
  quantityEntryRequired: boolean;
  quantityDisallowed: boolean;
  priceOverrideAllowed: boolean;
  notForSale: boolean;
  notForScoSale: boolean;
  pharmacyItem: boolean;
  availablePictures: PictureData[];
  attribtues: ItemAttribute[];
  itemIsOpen: boolean;
}
export interface ItemDetailsComplete {
  mainItem?: ItemDetail;
  variants?: ItemDetail[];
  variantType?: VariantType;
  variantCodes?: string[];
}

export interface Translation {
  languageISOCode: string;
  translatedText: string;
}
export interface PictureData {
  pICTURE_SIZE_SMALL: string;
  pICTURE_SIZE_MEDIUM: string;
  pICTURE_SIZE_LARGE: string;
  pictureSize: string;
  access: string;
  data: string;
}
export interface ItemAttribute {
  name: string;
  value: string;
}

export interface ApiTransactionListResult extends BasicResult {
  transactions: ApiTransaction[];
  rawReservations: ExternalTransaction[];
}
export interface ExternalTransaction {
  id: string;
  transactionUID: string;
  originalTransactionUID: string;
  name: string;
  typeCode: string;
  typeCodeId: number;
  created: string;
  expiration: string | null;
  status: DocumentStatus;
  isPackage: boolean | null;
  dontAffectStockQuantity: boolean | null;
  businessUnitInternalNumber: number;
  sourceBusinessUnitInternalNumber: number | null;
  destinationBusinessUnitInternalNumber: number | null;
  supplierNumber: string;
  supplierName: string;
  pOSNumber: number;
  documentNumber: string;
  documentPrice: number | null;
  currencyISOCode: string;
  operatorName: string;
  note: string;
  transactionSearchCode: string;
  items: ExternalTransactionItem[];
  payments: ExternalTransactionPayment[];
  customer: ExternalTransactionCustomer;
  jSONAdditionalDataHeader: string;
  externalTransactionNumber: string;
  transactionNumber: string;
}
export interface ExternalTransactionCustomer {
  firstName: string;
  surname: string;
  addressCode: string;
  street: string;
  city: string;
  postCode: string;
  phone: string;
  email: string;
  customerCard: string;
  customerNumber: string;
}
export interface ExternalTransactionPayment {
  paymentCode: string;
  currencyISOCode: string;
  value: number;
  referenceCode: string;
}
export interface ExternalTransactionItem {
  id: number;
  itemId: number;
  businessCode: string;
  eanCode: string;
  name: string;
  quantity: number;
  remainingQuantity: number;
  unitPrice: number;
  totalPrice: number;
  isDiscount: boolean;
  isAction: boolean;
  jSONAdditionalDataItem: string;
  taxValue: number | null;
  taxRate: number | null;
  taxBase: number | null;
  taxTotal: number | null;
  externalID: string;
  isNonDiscountable: boolean;
  note: string;
}
export enum DocumentStatus {
  Unknown = 0,
  Workspace = 1,
  Deleted = 2,
  ToApprove = 3,
  Rejected = 4,
  Approved = 5,
  Booked = 6,
  Void = 7,
  Corrective = 8,
  Interrupted = 9,
  Reverted = 10,
  Parked = 11,
  Canceled = 12,
  Delivery = 13,
  New = 14,
  NotPicked = 15,
  Picked = 16,
  Ready = 17,
  Uncofirmed = 18,
  BlockedInPda = 19,
}

export enum ApiTransactionType {
  Undefined = 0,
  EshopReservation = 1,
  Sale = 2,
}
export enum DiscountRequestType {
  Item = 0,
  Transaction = 1,
}

export interface PosAppConfig {
  address: string;
  isAndroid: boolean;
  portalApiAddress: string;
  apiTrace: boolean;
  appPort: number;
  country: string;
  isWebServer: boolean;
}

export interface ApiTransaction {
  id: string;
  transactionUID: string;
  originalTransactionUID: string;
  name: string;
  transactionType: ApiTransactionType;
  typeCode: string;
  created: string;
  expiration: string | null;
  status: ApiTransactionStatus;
  isPackage: boolean;
  dontAffectStockQuantity: boolean;
  businessUnitInternalNumber: number;
  sourceBusinessUnitInternalNumber: number | null;
  destinationBusinessUnitInternalNumber: number | null;
  supplierNumber: string;
  pOSNumber: number;
  documentNumber: string;
  documentPrice: number;
  currencyISOCode: string;
  operatorName: string;
  note: string;
  transactionSearchCode: string;
  externalTransactionNumber: string;
  items: ApiTransactionItem[];
  payments: ApiTransactionPayment[];
  customer: ApiTransactionCustomer;
  discounts: ApiTransactionDiscount[];
  jSONAdditionalDataHeader: string;
  reservationNumber: string | null;
}
export interface TrxRec {
  name: string;
  header: TrxRecHeader;
  components: TrxRecComponent;
}
export interface TrxRecHeader {
  creationNo: number;
  refToCreationNo: number | null;
  isVoid: boolean;
}
export interface TrxRecComponent {}

export enum ApiTransactionStatus {
  Unknown = 0,
  Workspace = 1,
  Deleted = 2,
  ToApprove = 3,
  Rejected = 4,
  Approved = 5,
  Booked = 6,
  Void = 7,
  Corrective = 8,
  Interrupted = 9,
  Reverted = 10,
  Parked = 11,
  Canceled = 12,
  Delivery = 13,
  New = 14,
  NotPicked = 15,
  Picked = 16,
  Ready = 17,
  Uncofirmed = 18,
}

export enum FinishTransactionErrors {
  FiscalizationActivity = "FiscalizationActivity",
  PrintingActivity = "PrintingActivity",
  TransactionNumberGeneratorActivity = "TransactionNumberGeneratorActivity",
  CountVatActivity = "CountVatActivity",
  CustomerActivity = "CustomerActivity",
  VoucherActivity = "VoucherActivity",
  UsedVoucherActivity = "UsedVoucherActivity",
  ReservationActivity = "ReservationActivity",
  BookingActivity = "BookingActivity",
}

export interface ApiTransactionCustomer {
  firstName?: string;
  surname?: string;
  addressCode?: string;
  street?: string;
  city?: string;
  postCode?: string;
  phone?: string;
  email?: string;
  customerCard?: string;
  customerNumber?: string;
  customerBonusPoints?: number;
  promotions?: ICustomerPromotion[];
}
export interface IAutomaticPromotion {
  id: number;
  name: string;
  count: number;
  promotionCode: string;
  giftImage: string;
  numberOfGifts: number;
  selected: boolean;
}

export interface IAutomaticPromotionDialogResponse {
  promotion: IAutomaticPromotion;
}

export interface IManualPromotion {
  ID: number;
  buttonText: string;
}

export interface ICustomerPromotion {
  active: boolean;
  name: string;
  description: string;
  from: string;
  to: string;
  promotionCode: string;
}

export interface ApiTransactionItem {
  itemID: number;
  businessCode: string;
  eanCode: string;
  name: string;
  quantity: number;
  remainingQuantity: number;
  unitPrice: number;
  totalPrice: number;
  isDiscount: boolean;
  isAction: boolean;
  isVoucher: boolean;
  jSONAdditionalDataItem: string;
  taxValue: number;
  taxRate: number;
  externalID: string;
  discounts: ApiTransactionDiscount[];
  itemIsOpen: boolean;
  variant: VariantTranslate[];
}
export interface ApiQuickSelectConfig {
  formConfig: ApiQuickSelectFormConfig;
}

export interface ApiQuickSelectFieldConfig {
  column: number;
  ean: string;
  itemIdentityId: number;
  icon: string;
  description: string;
}

export interface ApiQuickSelectCategoryConfig {
  icon: string;
  description: string;
  column: number;
  items: ApiQuickSelectFieldConfig[];
}

export interface ApiQuickSelectFormConfig {
  categories: ApiQuickSelectCategoryConfig[];
}
export interface DiscountItemEvent extends ApiTransactionItem {
  removeDiscount?: boolean;
  reopenDiscount?: boolean;
}
export interface VariantTranslate {
  variantType?: number;
  code?: string;
}

export interface ApiTransactionPayment {
  paymentName: string;
  paymentCode: string;
  currencyISOCode: string;
  value: number;
  paymentId: number;
  isCashback: boolean;
}

export enum DiscountType {
  Value = 0,
  Percent = 1,
}

export enum DiscountSource {
  ManualDiscount = 0,
  Promotion = 1,
}

export interface ApiTransactionDiscount {
  type: DiscountType;
  source: DiscountSource;
  code: string;
  name: string;
  value: number;
  calculatedValue: number;
}

export enum GenderType {
  Undefined,
  Man,
  Woman,
}

export interface Property {
  name: string;
  value: string;
}

export interface SignOnRequest {
  userNumber: number;
  pin: string;
  trainingMode: boolean;
}

export interface ProcessPaymentRequest extends SessionInfo {
  paymentAlreadyRelized: boolean;
  cardInfo: CustomerCardRequest;
  tenderType: PosTenderType;
  value: number;
  alternateEftIP: string;
  useAlternativeIpAddress: boolean;
  action: ReservationCashbackActionType;
  disableTerminalPrint: boolean;
}
export enum ReservationCashbackActionType {
  CASH,
  CARD,
  GIFT_CARD,
}
export interface PaymentRemoveResult extends BasicResult {}
export interface PaymentRemoveRequest extends SessionInfo {
  paymentId: number;
}
export enum PosTenderType {
  Cash = 0,
  Card = 1,
  Voucher = 2,
  BankTransfer = 3,
  CustomerPoints = 4,
  Virtual = 5,
  Cheque = 7,
  ElectronicVoucher = 8,
  Other = 9,
}
export interface ProcessCodeForVoucherRequest extends SessionInfo {
  code: CodeData;
  isPayment?: boolean;
  customValue?: number;
}

export interface ProcessCodeForVoucherResult extends BasicResult {
  message: string;
  data: VoucherInfo;
}
export interface PrintRequest extends SessionInfo {
  useSelfPrint: boolean;
  ipAddress: string;
}
export interface PrintResult extends BasicResult {}
export interface CustomerSearchRequest extends SessionInfo {
  cardNumber: string;
  customerNumber: string;
  email: string;
  phone: string;
}
export interface CustomerSearchResult extends BasicResult {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  cardNumber: string;
  contactList: Contact[];
  promotions: ICustomerPromotion[];
}
export interface Contact {
  iD: number;
  recUID: string;
  internalNumber: number;
  displayNumber: string;
  externalID: string;
  gLN: string;
  firstName: string;
  lastName: string;
  fullName: string;
  cardNumbers: string[];
  cardNumber: string;
  addressCode: string;
  addressStreet: string;
  addressStreetType: string;
  addressHouseNumber: string;
  addressZipCode: string;
  addressCity: string;
  addressCountry: string;
  address: string;
  email: string;
  phoneNumberMobile: string;
  matchCode: string;
  tinNumber: string;
  vatNumber: string;
  note1: string;
  note2: string;
  note3: string;
  fax: string;
  active: boolean;
  bonusPoints: number;
  bonusPointsToDiscountRatio: number;
  company: string;
  currencyISOCode: string;
  ilnNumber: string;
  countryCode: string;
  vATPayer: boolean;
  loyaltyLevel: number;
  loyaltyLevelText: string;
  loyaltyLevelValidTill: string | null;
  blockSendingEmails: boolean;
  jSONAdditionalDataContact: string;
  lastPurchaseDate: string | null;
  attributes: ContactAttribute[];
  options: ContactAttribute[];
  errorMessage: string;
  registrationRequired: boolean;
  qRCode: string;
}

export interface ContactAttribute {
  name: string;
  value: string;
}
export interface VoucherInfo {
  code: string;
  voucherValue: number;
  availableValue: number;
  voucherValueType: number | null;
  validFrom: string | null;
  validTo: string | null;
  name: string;
  voucherItemEAN: string;
  pinCode: string;
  voucherStatus: number | null;
  voucherType: number | null;
  currencyCode: string;
  lastChangeDate: string | null;
  active: boolean;
  rechargeable: boolean;
  reasons: string[];
  applicable: boolean;
  usableForProducts: string[];
}
export interface CustomerCardRequest extends SessionInfo {
  scanCode: string;
  msrData?: MsrData;
  rfidData?: RFIDData;
  scanDataType: number;
  entryMethod?: string;
}
export interface ProcessCustomerCardResult extends BasicResult {
  errorDetails: ErrorDetails;
}
export interface DiscountVerificationRequest extends SessionInfo {
  user: number;
  newUser: number;
  password: string;
  selectedDiscount: ManualDiscountType;
  card: string;
}
export interface VerifyDiscountResult extends BasicResult {
  isAuthorized: boolean;
}
export interface GoBackFromPaymentResult extends BasicResult {}
export interface TransactionFinishedResult extends BasicResult {
  numberOfReceipts: number;
}
export interface VoidTransactionResult extends BasicResult {}
export enum ScanDataType {
  Unknown = 0,
  Article = 1,
  Operator = 2,
  Customer = 3,
  Voucher = 4,
  Ticket = 5,
  Transaction = 6,
  Promotion = 7,
  PersonalCollection = 8,
}

export interface ScanCodeInfoResult extends BasicResult {
  scanDataType: ScanDataType;
  scannedCode: string;
  usedDecomposition: string;
  pin: string;
}
export interface ScanCodeInfoRequest extends SessionInfo {
  code: string;
}
export interface CodeData {
  code: string;
  agendaType: AgendaType;
}
export enum AgendaType {
  Unknown = 0,
  Article = 1,
  Operator = 2,
  Customer = 3,
  Voucher = 4,
  Ticket = 5,
  Transaction = 6,
  Promotion = 7,
  PersonalCollection = 8,
}
export interface VoucherInfoRequest extends SessionInfo {
  vOUCHER_UNSPECIFIED?: string;
  voucherType?: string;
  internalType?: string;
  currencyAmount?: CurrencyAmount;
  entryMethod?: string;
  voucherNumber: string;
  scanDataType?: number;
  storeId?: string;
}
export interface CurrencyAmount {
  amount: number;
  currencyCode: string;
}
export interface AddVoucherResult extends BasicResult {
  suggestedHandling: string;
  errorDetails: ErrorDetails;
}
export interface TransactionVoidRequest extends SessionInfo {
  reason: string;
}
export interface SignOnResult extends BasicResult {
  sessionID: string;
}
export interface PayWithCardResult extends BasicResult {
  errorDetails: ErrorDetails;
  message: string;
}
export interface ErrorDetails {
  customerInfo: Message;
  attendantInfo: Message;
}
export interface GoToPaymentResult extends BasicResult {
  foreignCurrencies: CurrencyDescriptor[];
}
export interface CurrencyDescriptor {
  currencyCode: string;
  fractionDigits: number;
  conversionFactor: number;
}

export interface StartTransactionFromExistingRequest
  extends StartTransactionRequest {
  transaction: ApiTransaction;
  rawReservation: ExternalTransaction;
}
export interface StartTransactionRequest extends SessionInfo {
  isReturn: boolean;
}

export interface StartTransactionRequest extends SessionInfo {
  isReturn: boolean;
}

export interface StartTransactionResult extends BasicResult {
  transactionStarted: boolean;
  transactionStartErrorMessage: string;
  isFullyPaid?: boolean;
}

export interface TransactionResult extends BasicResult {
  transaction: ApiTransaction;
}

export interface GetCurrencyCodeResult extends BasicResult {
  currencyCode: string;
}
export interface UnregisterCustomerResult extends BasicResult {
  errorDetails: ErrorDetails;
}

export interface ICompletedOrderRequest {
  documentNumber: string;
  documentPrice: number | null;
  payments: ISobitPayment[] | null;
  items: IItem[] | null;
}

export interface IEvent extends IShopInfo {
  targetClass: string;
  buttonClass: string;
  innerText: string;
  page: string;
}

export interface IEventTrackerRequest extends IEvent {
  serverUrl: string;
}

export interface IShopInfo {
  posNumber: number;
  businessUnitInternalNumber: number;
}
export interface ISendAppLogRequest extends IShopInfo {
  serverUrl: string;
}
export interface ISobitPayment {
  value: number;
  paymentCode: string;
}

export interface IItem {
  name: string;
  quantity: number;
  code: string;
  discounts: IDiscount[] | null;
}

export interface IDiscount {
  code: string | null;
  value: number | null;
  type: number | null;
  calculatedValue: number | null;
}
